/**
 * Deterministically fix a saved cover letter's header from the contract record.
 *
 * Older proposals were generated when the model filled in the date and recipient
 * itself, producing hallucinated values (wrong year, "John Doe", fake address,
 * doubled "RE:"). This patches the date (today), the recipient (real Contracting
 * Officer / office / mailing address from the contract) and the subject — with
 * NO API call — so existing proposals display correctly. Body text is untouched.
 */

function titleCase(s: string): string {
  return s.replace(/\b([A-Z]{2,})\b/g, (m) =>
    /^[0-9A-Z()]+$/.test(m) && m.length <= 6 ? m : m.charAt(0) + m.slice(1).toLowerCase()
  );
}

function cleanOfficeName(fullPath: string | null | undefined, agency: string | null | undefined): string | null {
  if (!fullPath) return null;
  const parts = String(fullPath).split('.').map((p) => p.trim()).filter(Boolean);
  const last = parts[parts.length - 1];
  if (last && last.toUpperCase() !== String(agency || '').toUpperCase()) return titleCase(last);
  return null;
}

interface NormalizedRecipient {
  name: string | null;
  title: string;
  office: string | null;
  agency: string | null;
  address: string;
  salutation: string;
}

export function buildRecipientFromContract(contract: any): NormalizedRecipient | null {
  if (!contract) return null;
  const co = contract.contracting_officer || {};
  const poc = contract.point_of_contact?.primary || {};
  const name = co.name || poc.name || null;
  const title = co.title || poc.title || 'Contracting Officer';

  const oa = contract.office_address || contract.metadata?.officeAddress || {};
  const cityStateZip = [oa.city, oa.state].filter(Boolean).join(', ') + (oa.zip ? ` ${oa.zip}` : '');
  const addressLines = [oa.street1, oa.street2, cityStateZip.trim(), oa.country && oa.country !== 'USA' ? oa.country : null]
    .map((l: any) => (l ? String(l).trim() : ''))
    .filter((l: string) => l.length > 0)
    .map(titleCase);

  const cleanName = name ? String(name).trim() : null;
  return {
    name: cleanName,
    title: title ? String(title).trim() : 'Contracting Officer',
    office: cleanOfficeName(contract.full_parent_path_name, contract.agency),
    agency: contract.agency || null,
    address: addressLines.join('\n'),
    salutation: cleanName ? `Dear ${cleanName}:` : 'Dear Contracting Officer:',
  };
}

/**
 * Returns a corrected cover-letter JSON string, or the input unchanged if it's
 * not a structured cover letter (e.g. plain-text basic proposals).
 */
export function normalizeCoverLetterString(coverLetterStr: unknown, contract: any): unknown {
  if (typeof coverLetterStr !== 'string' || !coverLetterStr.trim()) return coverLetterStr;

  let obj: any;
  try { obj = JSON.parse(coverLetterStr); } catch { return coverLetterStr; }
  if (!obj || typeof obj !== 'object' || !obj.body) return coverLetterStr;

  obj.date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const recipient = buildRecipientFromContract(contract);
  if (recipient) obj.recipient = recipient;

  const cleanTitle = String(contract?.title || obj?.subject?.contractTitle || '')
    .replace(/^\s*RE:\s*/i, '').trim();
  obj.subject = {
    contractTitle: cleanTitle,
    solicitationNumber:
      contract?.solicitation_number || obj?.subject?.solicitationNumber || null,
    naicsCode:
      contract?.naics_code ||
      (Array.isArray(contract?.naics_codes) ? contract.naics_codes[0] : null) ||
      obj?.subject?.naicsCode || null,
  };

  return JSON.stringify(obj);
}
